import { pushLeadEvent, pushBookingEvent } from "@/lib/gtmDataLayer";
import { useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import PhoneInput from "@/components/PhoneInput";
import StepBooking from "@/components/consultation/StepBooking";
import { supabase } from "@/integrations/supabase/client";
import { getAttribution } from "@/lib/attribution";
import {
  buildCalBookingUrl,
  createSubmissionId,
  saveLandingSubmission,
  type LandingFormValues,
} from "@/lib/consultationBooking";
import { trackInternalEvent } from "@/lib/track";
import { useLandingContent } from "@/lib/landing/LandingContentProvider";
import { phoneHref } from "@/lib/landing/resolve";

const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const STRICT_EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
export const landingFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Full name is required")
    .max(100, "Use 100 characters or fewer"),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .max(255)
    .email("Enter a valid email")
    .refine(
      (v) => STRICT_EMAIL_REGEX.test(v) && EMAIL_PATTERN.test(v),
      "Enter a complete email address",
    ),
  phone: z
    .string()
    .min(1, "Phone is required")
    .refine((v) => {
      try {
        return isPossiblePhoneNumber(v);
      } catch {
        return false;
      }
    }, "Enter a valid phone number"),
  companyName: z
    .string()
    .trim()
    .max(100, "Use 100 characters or fewer")
    .optional(),
  contactNote: z.string().optional(),
});
type FormValues = z.infer<typeof landingFormSchema>;

interface Props {
  variant: string;
  onStepChange: (step: 1 | 2) => void;
  onBookingHelpCall: () => void;
  /** Overrides used when the card is reused outside the landing page. */
  source?: { leadSource: string; formName: string; services: string[]; calService: string };
}

export default function LandingBookingCard({
  variant,
  onStepChange,
  onBookingHelpCall,
  source,
}: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [url, setUrl] = useState("");
  const [booked, setBooked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewNote, setPreviewNote] = useState(false);
  const { content, definition, preview } = useLandingContent();
  const geoPromise = useRef<Promise<{
    city: string | null;
    ip: string | null;
  }> | null>(null);
  const lastSubmission = useRef<{ fingerprint: string; id: string } | null>(
    null,
  );
  const form = useForm<FormValues>({
    resolver: zodResolver(landingFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      companyName: "",
      contactNote: "",
    },
  });

  const beginWarmup = () => {
    if (preview) return;
    if (!document.querySelector('link[data-landing-preconnect="cal"]')) {
      for (const href of ["https://cal.com", "https://app.cal.com"]) {
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = href;
        link.dataset.landingPreconnect = "cal";
        document.head.appendChild(link);
      }
    }
    if (!geoPromise.current) {
      geoPromise.current = supabase.functions
        .invoke("geoip-lookup", { method: "GET" })
        .then(({ data }) => ({
          city: data?.city ?? null,
          ip: data?.ip ?? null,
        }))
        .catch(() => ({ city: null, ip: null }));
    }
  };

  const calOpts = source ? { leadSource: source.leadSource, calService: source.calService } : undefined;
  const submit = async (raw: FormValues) => {
    if (raw.contactNote || preview) return;
    setSubmitting(true);
    const values: LandingFormValues = {
      name: raw.name.trim(),
      email: raw.email.trim(),
      phone: raw.phone,
      companyName: raw.companyName?.trim(),
    };
    const fingerprint = JSON.stringify(values);
    let submissionId =
      lastSubmission.current?.fingerprint === fingerprint
        ? lastSubmission.current.id
        : "";
    try {
      if (!submissionId) submissionId = createSubmissionId();
      beginWarmup();
      if (lastSubmission.current?.fingerprint !== fingerprint) {
        const geo = await Promise.race([
          geoPromise.current ?? Promise.resolve({ city: null, ip: null }),
          new Promise<{ city: null; ip: null }>((resolve) =>
            setTimeout(() => resolve({ city: null, ip: null }), 600),
          ),
        ]);
        await saveLandingSubmission(submissionId, values, geo, source ? { leadSource: source.leadSource, services: source.services } : undefined);
        void pushLeadEvent(source?.formName ?? "landing_booking", { name: values.name, email: values.email, phone: values.phone, company: values.companyName, city: geo.city }, source?.leadSource ?? definition.leadSource);
        lastSubmission.current = { fingerprint, id: submissionId };
      }
    } catch (error) {
      console.warn("Landing consultation could not be saved", error);
    } finally {
      // Saving, storage, and tracking must never prevent access to the calendar.
      try {
        const attribution = getAttribution();
        setUrl(
          buildCalBookingUrl(values, {
            ...(attribution?.last.params ?? {}),
            submissionId,
          }, calOpts),
        );
      } catch (error) {
        console.warn("Landing attribution unavailable", error);
        setUrl(buildCalBookingUrl(values, { submissionId }, calOpts));
      } finally {
        setStep(2);
        setSubmitting(false);
        onStepChange(2);
      }
    }
  };

  const inputClass =
    "h-12 w-full rounded-[10px] border border-landing-input bg-landing-light px-3.5 text-base md:text-base text-landing-ink placeholder:text-landing-card-muted focus-visible:ring-2 focus-visible:ring-landing-red focus-visible:ring-offset-1";
  const labelClass = "text-[13px] font-semibold text-landing-label";
  const errorClass = "text-landing-error";
  const editDetails = () => {
    setStep(1);
    onStepChange(1);
  };
  const editButton = (
    <Button
      type="button"
      variant="ghost"
      onClick={editDetails}
      className="h-11 shrink-0 px-2 text-landing-light-body hover:bg-landing-soft hover:text-landing-ink"
    >
      <ArrowLeft className="mr-1.5 h-4 w-4" />
      Edit my details
    </Button>
  );

  return (
    <section
      id="book"
      aria-label="Book your free call"
      className="landing-booking-card scroll-mt-3 rounded-2xl bg-landing-light p-5 text-landing-ink shadow-landing-card sm:rounded-[18px] sm:p-8"
    >
      <div className="mb-3.5 flex items-center text-[13.5px] font-semibold sm:mb-[18px]">
        <span
          className={`flex items-center gap-2 ${step === 1 ? "font-bold text-landing-ink" : "text-landing-red"}`}
        >
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[13px] font-bold sm:h-[26px] sm:w-[26px] ${step === 1 ? "bg-landing-red text-primary-foreground" : "border border-landing-red"}`}
          >
            {step === 1 ? "1" : <Check className="h-3.5 w-3.5" />}
          </span>
          Your details
        </span>
        <span className="mx-3 h-0.5 flex-1 bg-landing-step-border" />
        <span
          className={`flex items-center gap-2 ${step === 2 ? "font-bold text-landing-ink" : "text-landing-card-muted"}`}
        >
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[13px] font-bold sm:h-[26px] sm:w-[26px] ${step === 2 ? "bg-landing-red text-primary-foreground" : "bg-landing-soft"}`}
          >
            {booked ? <Check className="h-3.5 w-3.5" /> : "2"}
          </span>
          Pick a time
        </span>
      </div>
      {step === 1 ? (
        <>
          <h2 data-lp="booking.title" className="landing-card-title">{content.booking.title}</h2>
          <p data-lp="booking.subtitle" className="mt-1.5 hidden text-[15px] text-landing-card-muted sm:block">
            {content.booking.subtitle}
          </p>
          <Form {...form}>
            <form
              className="mt-3.5 space-y-3 sm:mt-[18px] sm:space-y-3.5"
              onSubmit={
                preview
                  ? (event) => {
                      event.preventDefault();
                      setPreviewNote(true);
                    }
                  : form.handleSubmit(submit)
              }
              onFocusCapture={beginWarmup}
              noValidate
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className={labelClass}>Full name</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="name"
                        maxLength={100}
                        className={inputClass}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage role="alert" className={errorClass} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className={labelClass}>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        maxLength={255}
                        autoComplete="email"
                        autoCapitalize="off"
                        spellCheck={false}
                        className={inputClass}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage role="alert" className={errorClass} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className={labelClass}>Phone</FormLabel>
                    <FormControl>
                      <PhoneInput
                        {...field}
                        withCountrySelect={false}
                        className={inputClass}
                      />
                    </FormControl>
                    <FormMessage role="alert" className={errorClass} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem className="hidden space-y-1.5 sm:block">
                    <FormLabel className={labelClass}>
                      Company{" "}
                      <span className="font-normal text-landing-light-muted">
                        (optional)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="organization"
                        maxLength={100}
                        className={inputClass}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage role="alert" className={errorClass} />
                  </FormItem>
                )}
              />
              <input
                {...form.register("contactNote")}
                className="sr-only"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <Button
                type="submit"
                disabled={submitting}
                aria-busy={submitting}
                className="h-[52px] w-full rounded-xl bg-landing-red text-[16.5px] font-bold text-primary-foreground hover:bg-landing-red-hover sm:h-14 sm:text-[17px]"
              >
                {submitting ? (
                  <>
                    <Loader2
                      aria-hidden="true"
                      className="h-5 w-5 animate-spin"
                    />
                    <span className="sr-only">Loading</span>
                  </>
                ) : (
                  <>
                    <span data-lp="booking.button">{content.booking.button}</span>{" "}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
              <p className="hidden items-center justify-center gap-1.5 text-[13px] text-landing-card-muted sm:flex">
                <Lock className="h-3.5 w-3.5" />
                <span data-lp="booking.reassurance">{content.booking.reassurance}</span>
              </p>
              {previewNote && (
                <p role="status" className="text-center text-sm font-semibold text-landing-ink">
                  Preview only. The form is turned off.
                </p>
              )}
            </form>
          </Form>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4">
            <h2 className="landing-card-title">
              {booked ? "You are booked" : "Pick a time that works for you"}
            </h2>
            <div className="hidden sm:block">{editButton}</div>
          </div>
          <p
            aria-live="polite"
            className="mt-1.5 text-[15px] text-landing-card-muted"
          >
            {booked
              ? `We sent a calendar invite to ${form.getValues("email")}. Talk soon.`
              : "Choose a day and time. We will send a calendar invite to your email."}
          </p>
          <div className="mt-1 sm:hidden">{editButton}</div>
          <div className="-mx-5 mt-3.5 sm:mx-0 sm:mt-[18px]">
            <StepBooking
              variant="landing"
              hideEditDetails
              url={url}
              onBack={editDetails}
              onBooked={() => {
                if (booked) return;
                setBooked(true);
                trackInternalEvent("booking_completed", "Cal.com booking confirmed");
              }}
            />
          </div>
          {!booked && (
            <p className="mt-4 text-center text-sm text-landing-light-body">
              Can't find a time that works?{" "}
              <a
                href={phoneHref(content.phone)}
                onClick={onBookingHelpCall}
                className="font-bold underline"
              >
                {`Call ${content.phone}`}
              </a>
            </p>
          )}
        </>
      )}
    </section>
  );
}
